import React, { useEffect, useMemo, useState } from "react";
import GameIdButton from "components/Host/GameIdButton";
import {
  ROLE_NAMES,
  getActLabel,
  type GameState,
  type PlayerKey,
  type Phase,
  type PhaseTimerStatus,
  type TimerSettings,
  type WaterManagement,
  MIN_PHASE_TIMER_DURATION_MS,
  MAX_PHASE_TIMER_DURATION_MS,
} from "../vowGameLogic";
import { BUTTON_PRIMARY, BUTTON_ALERT, BUTTON_MUTED, PANEL_CLASS } from "./uiConstants";
import { WATER_MANAGEMENT_CHOICES, WATER_MANAGEMENT_LABELS } from "./waterManagementOptions";
import { formatCountdown, toMinutes, toMilliseconds } from "../utils/time";
import ScoreSummaryGrid from "./gameboard/ScoreSummaryGrid";

export type HostRosterPlayer = {
  id: number;
  playerName?: string;
  role?: PlayerKey | null;
};

type HostAdminPanelProps = {
  gameState: GameState;
  timerStatus: PhaseTimerStatus | null;
  timerSettings: TimerSettings;
  hostId: number | null;
  gameId: string;
  sortedPlayers: HostRosterPlayer[];
  onFinalizeActions: () => void;
  onRunSimulation: () => void;
  onResetGame: () => void;
  onWaterManagementChange: (value: WaterManagement) => void;
  onAdjustTimer: (deltaMs: number) => void;
  onUpdateTimerSettings: (updates: Partial<TimerSettings>) => void;
  onExtendGame: () => void;
  onEndGame: () => void;
  onDownloadGameData: () => void;
  onViewAnalysis: () => void;
};

const TIMER_LABELS: Record<Phase, string> = {
  actions: "Planning",
  simulation: "Voting",
  ended: "Session",
};

const HostAdminPanel: React.FC<HostAdminPanelProps> = ({
  gameState,
  timerStatus,
  timerSettings,
  hostId,
  gameId,
  sortedPlayers,
  onFinalizeActions,
  onRunSimulation,
  onResetGame,
  onWaterManagementChange,
  onAdjustTimer,
  onUpdateTimerSettings,
  onExtendGame,
  onEndGame,
  onDownloadGameData,
  onViewAnalysis,
}) => {
  const isGameEnded = gameState.gameEnded;
  const atDefaultEnd = gameState.reachedDefaultEnding && !gameState.gameEnded;
  const canFinalize = gameState.phase === "actions" && !isGameEnded;
  const canSimulate =
    gameState.phase === "simulation" && !isGameEnded && !atDefaultEnd;
  const showEndGameButton =
    !isGameEnded && (gameState.reachedDefaultEnding || gameState.hasExtendedGame);
  const hasHistory = gameState.history.length > 0;
  const [actionsMinutes, setActionsMinutes] = useState(() => toMinutes(timerSettings.actionsMs));
  const [simulationMinutes, setSimulationMinutes] = useState(() =>
    toMinutes(timerSettings.simulationMs)
  );

  useEffect(() => {
    setActionsMinutes(toMinutes(timerSettings.actionsMs));
    setSimulationMinutes(toMinutes(timerSettings.simulationMs));
  }, [timerSettings.actionsMs, timerSettings.simulationMs]);

  const desiredActionMs = useMemo(
    () => toMilliseconds(actionsMinutes),
    [actionsMinutes]
  );
  const desiredSimulationMs = useMemo(
    () => toMilliseconds(simulationMinutes),
    [simulationMinutes]
  );
  const defaultsChanged =
    desiredActionMs !== timerSettings.actionsMs ||
    desiredSimulationMs !== timerSettings.simulationMs;

  const timerPhaseLabel = timerStatus
    ? TIMER_LABELS[timerStatus.phase] ?? "Timer"
    : "Timer";
  const timerRemainingDisplay = timerStatus
    ? timerStatus.hasExpired
      ? "Expired"
      : formatCountdown(timerStatus.remainingMs)
    : "—";
  const timerTotalDisplay = timerStatus
    ? formatCountdown(timerStatus.totalMs)
    : formatCountdown(timerSettings.actionsMs);
  const timerTone = timerStatus?.hasExpired
    ? "text-rose-700"
    : timerStatus && timerStatus.remainingMs <= 30_000
      ? "text-amber-700"
      : "text-slate-900";
  const canAdjustTimer =
    Boolean(timerStatus) &&
    !timerStatus?.locked &&
    timerStatus?.phase !== "ended" &&
    !isGameEnded;

  const minMinutes = useMemo(
    () => toMinutes(MIN_PHASE_TIMER_DURATION_MS, 2),
    []
  );
  const maxMinutes = useMemo(
    () => toMinutes(MAX_PHASE_TIMER_DURATION_MS, 0),
    []
  );
  const minMinutesText = useMemo(
    () => minMinutes.toFixed(2).replace(/\.?0+$/, ""),
    [minMinutes]
  );
  const maxMinutesText = useMemo(
    () => Math.round(maxMinutes).toString(),
    [maxMinutes]
  );

  const handleDefaultsSubmit = () => {
    const updates: Partial<TimerSettings> = {};
    if (desiredActionMs > 0) {
      updates.actionsMs = desiredActionMs;
    }
    if (desiredSimulationMs > 0) {
      updates.simulationMs = desiredSimulationMs;
    }
    if (Object.keys(updates).length === 0) return;
    onUpdateTimerSettings(updates);
  };
  const handleActionsMinutesChange = (value: string) => {
    const parsed = Number(value);
    setActionsMinutes(Number.isFinite(parsed) ? parsed : 0);
  };
  const handleSimulationMinutesChange = (value: string) => {
    const parsed = Number(value);
    setSimulationMinutes(Number.isFinite(parsed) ? parsed : 0);
  };
  const timerButtonClass = (enabled: boolean) =>
    enabled
      ? "border-2 border-black bg-[#f4d58d] px-3 py-2 text-xs font-semibold text-slate-900 transition-colors hover:bg-[#f0c85b]"
      : "border-2 border-black bg-[#d1c7b0] px-3 py-2 text-xs font-semibold text-slate-700 cursor-not-allowed";

  return (
    <section className={`${PANEL_CLASS} p-6`}>
    <h2 className="text-2xl font-bold text-slate-900">Host Admin</h2>
    <p className="mt-1 text-sm text-slate-800">
      You are hosting this session. Monitor the board, track player roles, and advance the
      game when everyone is ready.
    </p>

    <div className="mt-4 space-y-4">
      <div>
        <ScoreSummaryGrid scores={gameState.scores} />
        <p className="mt-1 text-[11px] text-slate-700">
          Metrics update at the close of each act so you can narrate the ecosystem&apos;s progress.
        </p>
      </div>

      <div className="border-2 border-black bg-[#dcd2b5] p-4">
        <h3 className="text-lg font-semibold text-slate-900">Invite Code</h3>
        <p className="mt-1 text-sm text-slate-800">
          Share this code so new players can join your session.
        </p>
        <div className="mt-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
          <GameIdButton gameId={gameId} />
          <p className="text-xs text-slate-700">
            {gameId
              ? "Click the code to copy it to the clipboard."
              : "Generating invite code…"}
          </p>
        </div>
      </div>

      <div className="border-2 border-black bg-[#dcd2b5] p-4">
        <h3 className="text-lg font-semibold text-slate-900">Player Roster</h3>
        <ul className="mt-3 divide-y divide-black/30 border border-black bg-[#faf3e2] text-sm">
          {sortedPlayers.length === 0 && (
            <li className="px-3 py-2 text-slate-700">No players connected yet.</li>
          )}
          {sortedPlayers.map((player) => (
            <li key={player.id} className="flex items-center justify-between px-3 py-2">
              <div>
                <p className="font-semibold text-slate-900">
                  {player.playerName || (player.id === hostId ? "Host" : `Player ${player.id}`)}
                </p>
                <p className="text-[11px] uppercase tracking-wide text-slate-700">
                  ID: {player.id}
                </p>
              </div>
              <p className="text-xs font-semibold uppercase text-slate-700">
                {player.id === hostId
                  ? "Host"
                  : player.role
                    ? ROLE_NAMES[player.role as PlayerKey]
                    : "Role not selected"}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-4 border-2 border-black bg-[#dcd2b5] p-4">
        <h3 className="text-lg font-semibold text-slate-900">Phase Controls</h3>
        <p className="text-sm text-slate-800">
          Current Phase:{" "}
          <span className="font-semibold capitalize">{gameState.phase === "simulation" ? "Voting" : gameState.phase}</span>
        </p>
        <p className="text-sm text-slate-800">
          Timeline: <span className="font-semibold">{getActLabel(gameState.annee)}</span>
        </p>
        <div className="rounded border border-black bg-[#faf3e2] p-4">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-800">
              Phase Timer
            </p>
            <span className={`text-sm font-semibold ${timerTone}`}>
              {timerPhaseLabel}: {timerRemainingDisplay}
            </span>
          </div>
          <p className="text-xs text-slate-700">
            Total window: {timerTotalDisplay}. Players pause once the timer expires.
          </p>
          {timerStatus?.hasExpired && (
            <p className="text-xs font-semibold text-rose-700">
              Timer expired — players are waiting for you to advance the game.
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onAdjustTimer(-60_000)}
              disabled={!canAdjustTimer}
              className={timerButtonClass(canAdjustTimer)}
            >
              -1 min
            </button>
            <button
              type="button"
              onClick={() => onAdjustTimer(-30_000)}
              disabled={!canAdjustTimer}
              className={timerButtonClass(canAdjustTimer)}
            >
              -30 sec
            </button>
            <button
              type="button"
              onClick={() => onAdjustTimer(30_000)}
              disabled={!canAdjustTimer}
              className={timerButtonClass(canAdjustTimer)}
            >
              +30 sec
            </button>
            <button
              type="button"
              onClick={() => onAdjustTimer(60_000)}
              disabled={!canAdjustTimer}
              className={timerButtonClass(canAdjustTimer)}
            >
              +1 min
            </button>
          </div>
          {!canAdjustTimer && (
            <p className="mt-2 text-[11px] font-semibold text-slate-700">
              Adjustments disabled while the timer is locked.
            </p>
          )}
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">
              Planning default (minutes)
              <input
                type="number"
                min={minMinutes}
                max={maxMinutes}
                step={0.25}
                value={actionsMinutes}
                onChange={(event) => handleActionsMinutesChange(event.target.value)}
                className="mt-1 w-full border border-black bg-white px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-black"
              />
            </label>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-700">
              Voting default (minutes)
              <input
                type="number"
                min={minMinutes}
                max={maxMinutes}
                step={0.25}
                value={simulationMinutes}
                onChange={(event) => handleSimulationMinutesChange(event.target.value)}
                className="mt-1 w-full border border-black bg-white px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-black"
              />
            </label>
          </div>
          <div className="mt-3 space-y-1">
            <button
              type="button"
              onClick={handleDefaultsSubmit}
              disabled={!defaultsChanged}
              className={`w-full ${
                defaultsChanged
                  ? BUTTON_MUTED
                  : "border-2 border-black bg-[#d1c7b0] px-2 py-1 text-xs font-semibold text-slate-700 cursor-not-allowed"
              }`}
            >
              Update Default Durations
            </button>
            <p className="text-[11px] text-slate-700">
              Allowed range {minMinutesText} – {maxMinutesText} minutes.
            </p>
          </div>
        </div>

        {isGameEnded && (
          <p className="text-sm font-semibold text-red-700">
            The game has ended. Reset to start a new session.
          </p>
        )}

        {gameState.phase === "simulation" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="wm-choice-admin"
                className="block text-sm font-medium text-slate-800"
              >
                Host selection - Water Management (WM):
              </label>
              <select
                id="wm-choice-admin"
                value={gameState.wmChoice}
                onChange={(event) => onWaterManagementChange(event.target.value as WaterManagement)}
                className="w-full border border-black bg-[#faf3e2] px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-black"
              >
                {WATER_MANAGEMENT_CHOICES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <WaterManagementVoteSummary
              wmChoice={gameState.wmChoice}
              wmVotes={gameState.wmVotes}
              players={sortedPlayers}
              hostId={hostId}
              onApplyMajority={onWaterManagementChange}
            />
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            id="host-finalize-actions"
            type="button"
            onClick={onFinalizeActions}
            disabled={!canFinalize}
            className={`w-full sm:w-1/2 ${
              !canFinalize
                ? "border-2 border-black bg-[#d1c7b0] px-4 py-3 text-sm font-semibold text-slate-700 cursor-not-allowed"
                : BUTTON_PRIMARY
            }`}
          >
            Move to Voting
          </button>
          {atDefaultEnd ? (
            <button
              id="host-extend-game"
              type="button"
              onClick={onExtendGame}
              className={`w-full sm:w-1/2 ${BUTTON_PRIMARY}`}
            >
              Extend Game
            </button>
          ) : (
            <button
              id="host-run-simulation"
              type="button"
              onClick={onRunSimulation}
              disabled={!canSimulate}
              className={`w-full sm:w-1/2 ${
                !canSimulate
                  ? "border-2 border-black bg-[#d1c7b0] px-4 py-3 text-sm font-semibold text-slate-700 cursor-not-allowed"
                  : BUTTON_PRIMARY
              }`}
            >
              Simulate Next Act
            </button>
          )}
        </div>

        {showEndGameButton && (
          <button
            id="host-end-game"
            type="button"
            onClick={onEndGame}
            className={`w-full ${BUTTON_ALERT}`}
          >
            End Game
          </button>
        )}

        {isGameEnded && (
          <div className="space-y-2">
            <button
              id="host-view-analysis"
              type="button"
              onClick={onViewAnalysis}
              className={`w-full ${BUTTON_PRIMARY}`}
            >
              View Analysis Dashboard
            </button>
            <button
              id="host-download-game-data"
              type="button"
              onClick={onDownloadGameData}
              disabled={!hasHistory}
              className={`w-full ${
                hasHistory
                  ? BUTTON_MUTED
                  : "border-2 border-black bg-[#d1c7b0] px-4 py-2 text-sm font-semibold text-slate-700 cursor-not-allowed"
              }`}
            >
              Download Game Data
            </button>
            {!hasHistory && (
              <p className="text-xs text-slate-700">
                Simulation data becomes available after at least one act is completed.
              </p>
            )}
          </div>
        )}
      </div>
    </div>

    <button
      id="reset-game"
      type="button"
      onClick={onResetGame}
      className={`w-full ${BUTTON_ALERT} mt-4`}
    >
      Reset Game
    </button>
  </section>
  );
};

type WaterManagementVoteSummaryProps = {
  wmChoice: WaterManagement;
  wmVotes: Record<number, WaterManagement>;
  players: HostRosterPlayer[];
  hostId: number | null;
  onApplyMajority: (value: WaterManagement) => void;
};

const WaterManagementVoteSummary: React.FC<WaterManagementVoteSummaryProps> = ({
  wmChoice,
  wmVotes,
  players,
  hostId,
  onApplyMajority,
}) => {
  const voteEntries = Object.entries(wmVotes ?? {}) as Array<[string, WaterManagement]>;
  const counts: Record<WaterManagement, number> = {
    "1": 0,
    "2": 0,
    "3": 0,
    "4": 0,
    "5": 0,
  };

  voteEntries.forEach(([, value]) => {
    counts[value] = (counts[value] ?? 0) + 1;
  });

  let leadingValue: WaterManagement | null = null;
  let leadingCount = 0;
  let isTie = false;

  (Object.entries(counts) as Array<[WaterManagement, number]>).forEach(([value, count]) => {
    if (count === 0) return;
    if (count > leadingCount) {
      leadingCount = count;
      leadingValue = value;
      isTie = false;
    } else if (count === leadingCount) {
      isTie = true;
    }
  });

  const majorityValue = !isTie && leadingCount > 0 ? leadingValue : null;
  const totalVotes = voteEntries.length;
  const voteStatus = (() => {
    if (!totalVotes) return "No votes submitted yet.";
    if (majorityValue) {
      const suffix = leadingCount === 1 ? "vote" : "votes";
      return `Current vote outcome: ${WATER_MANAGEMENT_LABELS[majorityValue]} (${leadingCount} ${suffix}).`;
    }
    return "Votes are currently tied. Choose a WM regime manually.";
  })();

  const hostMatchesMajority = Boolean(majorityValue && majorityValue === wmChoice);

  const eligiblePlayers = players.filter((player) => player.id !== hostId);
  const totalEligiblePlayers = eligiblePlayers.length;
  const submittedVoteCount = eligiblePlayers.reduce((acc, player) => {
    return acc + (wmVotes[player.id] ? 1 : 0);
  }, 0);
  const pendingVoteCount = Math.max(totalEligiblePlayers - submittedVoteCount, 0);

  const knownPlayerIds = new Set<number>();
  players.forEach((player) => {
    knownPlayerIds.add(player.id);
  });
  if (typeof hostId === "number" && hostId > 0) {
    knownPlayerIds.add(hostId);
  }

  const playerVoteRows = players
    .filter((player) => player.id !== hostId)
    .map((player) => ({
      id: player.id,
      displayName: player.playerName || `Player ${player.id}`,
      role: player.role ?? null,
      vote: wmVotes[player.id] ?? null,
      isExternal: false,
    }));

  const extraVoteRows = voteEntries
    .filter(([key]) => !knownPlayerIds.has(Number(key)))
    .map(([key, value]) => ({
      id: Number(key),
      displayName: `Player ${key}`,
      role: null as PlayerKey | null,
      vote: value,
      isExternal: true,
    }));

  const allVoteRows = [...playerVoteRows, ...extraVoteRows];

  return (
    <div className="space-y-3 rounded-lg border border-black bg-[#faf3e2] p-4">
      <div className="text-sm text-slate-800">
        <p>
          Host selection:{" "}
          <span className="font-semibold text-slate-900">
            {WATER_MANAGEMENT_LABELS[wmChoice]}
          </span>
        </p>
        <p className="mt-1 text-xs text-slate-700">{voteStatus}</p>
        {totalEligiblePlayers > 0 && (
          <p className="mt-1 text-xs text-slate-700">
            Votes submitted: {submittedVoteCount} / {totalEligiblePlayers}
            {pendingVoteCount > 0
              ? ` (${pendingVoteCount} pending)`
              : " (all players have voted)"}
          </p>
        )}
        {majorityValue && !hostMatchesMajority && (
          <p className="mt-1 text-xs font-semibold text-amber-700">
            Majority differs from your current selection.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2 text-xs text-slate-800 sm:grid-cols-2">
        {WATER_MANAGEMENT_CHOICES.map((option) => {
          const count = counts[option.value] ?? 0;
          return (
            <div
              key={option.value}
              className="flex items-center justify-between rounded border border-black/20 bg-white px-2 py-1"
            >
              <span className="font-semibold text-slate-900">{option.shortLabel}</span>
              <span className="text-xs font-semibold text-slate-700">
                {count} {count === 1 ? "vote" : "votes"}
              </span>
            </div>
          );
        })}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
          Player votes
        </p>
        {allVoteRows.length === 0 ? (
          <p className="mt-1 text-xs text-slate-600">No players connected.</p>
        ) : (
          <ul className="mt-1 divide-y divide-black/20 border border-black/20 bg-white text-xs">
            {allVoteRows.map((row) => {
              const voteLabel = row.vote ? WATER_MANAGEMENT_LABELS[row.vote] : "No vote yet";
              const roleLabel = row.role ? ROLE_NAMES[row.role] : null;
              return (
                <li key={row.id} className="flex items-center justify-between px-2 py-1">
                  <div>
                    <p className="font-semibold text-slate-900">{row.displayName}</p>
                    {roleLabel && (
                      <p className="text-[10px] uppercase tracking-wide text-slate-600">
                        {roleLabel}
                      </p>
                    )}
                    {row.isExternal && (
                      <p className="text-[10px] uppercase tracking-wide text-amber-700">
                        Vote received (player not in roster)
                      </p>
                    )}
                  </div>
                  <p className="text-right text-xs font-semibold text-slate-700">{voteLabel}</p>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          if (majorityValue && majorityValue !== wmChoice) {
            onApplyMajority(majorityValue);
          }
        }}
        disabled={!majorityValue || majorityValue === wmChoice}
        className={`w-full ${
          majorityValue && majorityValue !== wmChoice
            ? BUTTON_PRIMARY
            : "border-2 border-black bg-[#d1c7b0] px-4 py-2 text-sm font-semibold text-slate-700 cursor-not-allowed"
        }`}
      >
        {majorityValue
          ? hostMatchesMajority
            ? "Host selection already matches vote"
            : `Use Majority Vote (${WATER_MANAGEMENT_LABELS[majorityValue]})`
          : "Awaiting clear vote outcome"}
      </button>
    </div>
  );
};

export default HostAdminPanel;
